import React, { useState } from 'react';
import { Check, Edit2, Trash2, TrendingUp, Droplets, Footprints, Moon, Brain, Utensils, Book, Dumbbell, Heart, Smile, Coffee } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';
import confetti from 'canvas-confetti';

const ICON_MAP = {
  Droplets, Footprints, Moon, Brain, Utensils, Book, Dumbbell, Heart, Smile, Coffee
};

const HabitList = ({ habits, completions, onToggleCompletion, onEdit, onDelete, date = new Date() }) => {
  const [quantityDialog, setQuantityDialog] = useState(null);
  const [quantityValue, setQuantityValue] = useState('');

  const todayStr = date.toISOString().split('T')[0];

  const getCompletionToday = (habitId) => {
    return completions.find(c => c.habitId === habitId && c.date === todayStr);
  };

  const getCurrentStreak = (habitId) => {
    let streak = 0;
    const date = new Date();

    while (true) {
      const dateStr = date.toISOString().split('T')[0];
      const completed = completions.some(c => c.habitId === habitId && c.date === dateStr);

      if (completed) {
        streak++;
        date.setDate(date.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#4ECDC4', '#FF6B6B', '#FFB088', '#95A6F8', '#7BC9A6']
    });
  };

  const handleCheckboxClick = (habit) => {
    const completion = getCompletionToday(habit.id);

    if (completion) {
      // Desmarcar
      onToggleCompletion(habit.id, date, null);
    } else if (habit.target) {
      // Tem meta - abrir dialog
      setQuantityDialog(habit);
      setQuantityValue('');
    } else {
      // Sem meta - marcar direto
      onToggleCompletion(habit.id, date, null);
      triggerConfetti();
    }
  };

  const handleQuantitySubmit = () => {
    if (!quantityDialog || !quantityValue) return;

    const value = parseFloat(quantityValue);
    if (value > 0) {
      onToggleCompletion(quantityDialog.id, date, value);

      // Se atingiu a meta, fazer confetti
      if (value >= quantityDialog.target) {
        triggerConfetti();
      }

      setQuantityDialog(null);
      setQuantityValue('');
    }
  };

  const filteredHabits = habits.filter(habit => {
    // Legacy support: if configured as weekly/monthly but no specific rules, show always (or maybe allow user to fix)
    // Actually, user wants to fix the "show always" bug. 
    // If strict:
    if (habit.type === 'weekly') {
      return habit.weekDays?.includes(date.getDay());
    }
    if (habit.type === 'monthly') {
      return habit.monthDay === date.getDate();
    }
    return true;
  });

  if (habits.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
          <TrendingUp className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Nenhum hábito ainda</h3>
        <p className="text-muted-foreground">Crie seu primeiro hábito para começar!</p>
      </div>
    );
  }

  if (filteredHabits.length === 0) {
    return (
      <div className="text-center py-16 opacity-70">
        <h3 className="text-lg font-medium text-foreground mb-2">Tudo limpo por hoje!</h3>
        <p className="text-muted-foreground">Nenhum hábito agendado para esta data.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {filteredHabits.map((habit) => {
          const completion = getCompletionToday(habit.id);
          const streak = getCurrentStreak(habit.id);
          const Icon = ICON_MAP[habit.icon] || Droplets;

          const isCompleted = completion !== undefined;
          const progressPercentage = habit.target && completion?.value
            ? Math.min((completion.value / habit.target) * 100, 100)
            : isCompleted ? 100 : 0;

          return (
            <Card
              key={habit.id}
              className="p-4 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleCheckboxClick(habit)}
                  className={cn(
                    'flex-shrink-0 w-10 h-10 rounded-lg border-2 transition-all duration-200',
                    'flex items-center justify-center hover:scale-105 relative overflow-hidden',
                    isCompleted
                      ? 'border-transparent'
                      : 'border-border hover:border-emerald-400'
                  )}
                  style={{
                    backgroundColor: isCompleted ? habit.color : 'transparent',
                  }}
                >
                  {habit.target && isCompleted ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                      <div
                        className="absolute inset-0 bg-white/30"
                        style={{ width: `${progressPercentage}%` }}
                      />
                      <Icon className="w-5 h-5 text-white relative z-10" />
                    </div>
                  ) : isCompleted ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <Icon className="w-5 h-5" style={{ color: habit.color }} />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={cn(
                      'font-medium transition-all duration-200',
                      isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'
                    )}>
                      {habit.name}
                    </h3>
                    {habit.category && (
                      <span
                        className="px-2 py-0.5 text-xs rounded-full"
                        style={{
                          backgroundColor: `${habit.color}20`,
                          color: habit.color,
                        }}
                      >
                        {habit.category}
                      </span>
                    )}
                    {habit.type && habit.type !== 'daily' && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-secondary text-secondary-foreground">
                        {habit.type === 'weekly' ? 'Semanal' : 'Mensal'}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    {streak > 0 && (
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        <span>{streak} dias</span>
                      </div>
                    )}
                    {habit.target && (
                      <div>
                        Meta: {completion?.value || 0} / {habit.target} {habit.unit}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(habit)}
                    className="h-8 w-8"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(habit.id)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!quantityDialog} onOpenChange={() => setQuantityDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar {quantityDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantidade</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="quantity"
                  type="number"
                  value={quantityValue}
                  onChange={(e) => setQuantityValue(e.target.value)}
                  placeholder={`Meta: ${quantityDialog?.target}`}
                  autoFocus
                  min="0"
                  step="any"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {quantityDialog?.unit}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Meta diária: {quantityDialog?.target} {quantityDialog?.unit}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleQuantitySubmit} className="flex-1">
                Registrar
              </Button>
              <Button variant="outline" onClick={() => setQuantityDialog(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HabitList;
